/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import React from "react";

import {
	Animate,
	BackgroundContainer,
	ButtonList,
	CosmosButtonProps,
	CosmosText,
	EdsIcon,
	ResponsiveContainer,
	SectionContainer,
	TitleDescStack,
	Wrap,
} from "@components/elements";

import { CosmosEnumNumberCalloutVariants, NumberCalloutSectionFragment } from "@cosmos/hygraph-schema";
import { extractBackgroundContainerProps, extractSectionContainerProps } from "@components/utils/section.utils";
import { css, cx } from "@panda/css";
import { Box, Flex, VStack } from "@panda/jsx";
import { ContentLargeImageMedia } from "../content-large-image/ContentLargeImage.Media";
import { animateCustomCss, assetWrapStyles } from "../content-large-image/ContentLargeImage.section.styles";
import { NumberCalloutList } from "./NumberCalloutList.molecule";

export interface NumberCalloutSectionProps extends Omit<NumberCalloutSectionFragment, "__typename" | "id"> {
	sectionIndex?: number;
	/** True when a page hero renders above this section — then even the first section is an h2, not the page h1. */
	hasHero?: boolean;
}

const sectionStyles = css({
	minHeight: "100%",
	md: {
		minHeight: "540px",
	},
	xl: {
		minHeight: "650px",
	},
	"&&": { smDown: { alignItems: "start" } },
});

const pageHeightStyles = css({
	height: "calc(100vh - 72px)",
	overflowX: "clip",
	contentVisibility: "unset",
	minHeight: "540px",
	smDown: {
		height: "100%",
		minHeight: "calc(100vh - 72px)",
	},
});

const iconStyles = css({
	"& svg": {
		height: "eds.dimension.80!important", //override EDS style
		width: "eds.dimension.80!important",
	},
});

export const NumberCalloutSection = ({
	config,
	eyebrow,
	title,
	desc,
	callouts,
	numberCalloutVariant,
	timeAndDate,
	buttons,
	sectionIndex,
	hasHero,
	icon,
	iconGradient,
	titleGradient,
	asset,
	flip,
	verticallyCenter,
}: NumberCalloutSectionProps): React.JSX.Element => {
	const isFirstSection = sectionIndex === 0;
	// The page h1 belongs to the hero; this section owns it only when first and no hero
	// renders above. Visual size (variant) still keys on isFirstSection — unchanged.
	const isPageTitle = isFirstSection && !hasHero;
	const isPageHeight = isFirstSection && config?.backgroundFullHeight;
	// The ContentLargeImage variant unlocks asset/flip/verticallyCenter in Hygraph
	// (the CMS hides those fields unless this variant is selected). Component keys
	// the layout off the same enum so behavior matches author intent.
	const isContentLargeImageVariant = numberCalloutVariant === CosmosEnumNumberCalloutVariants.ContentLargeImage;
	const hasAsset = Boolean(asset?.url);

	const cosmosButtons: CosmosButtonProps[] =
		buttons?.map((button) => {
			return {
				...button,
				size: "lg",
			};
		}) || [];

	const content = (
		<VStack
			alignItems={isContentLargeImageVariant ? { smDown: "center", sm: "start" } : "start"}
			textAlign={isContentLargeImageVariant ? { smDown: "center", sm: "start" } : undefined}
			gap={isContentLargeImageVariant ? { smDown: "eds.dimension.20", sm: "eds.dimension.32" } : "eds.dimension.32"}
			width="100%"
			maxWidth="614px"
			paddingTop={{ xs: isPageHeight && !verticallyCenter ? "eds.dimension.64" : undefined, xsDown: "0" }}
			className={cx("number-callout-section")}
		>
			{icon ? (
				<Box className={iconStyles}>
					<EdsIcon size="xl" icon={icon} gradient={iconGradient} />
				</Box>
			) : null}
			{eyebrow ? <CosmosText variant="eyebrowLG" text={eyebrow} /> : null}
			{title || desc ? (
				<TitleDescStack
					stack={{ gap: "eds.dimension.16" }}
					gradient={titleGradient}
					title={{
						...title,
						as: isPageTitle ? "h1" : "h2",
						variant: isFirstSection ? "heading2XL" : "headingXL",
					}}
					description={{ html: desc, color: "eds.text.primary" }}
				/>
			) : null}
			{callouts?.length || timeAndDate || numberCalloutVariant ? (
				<NumberCalloutList callouts={callouts} numberCalloutVariant={numberCalloutVariant} timeAndDate={timeAndDate} />
			) : null}
			{cosmosButtons?.length ? <ButtonList buttons={cosmosButtons} /> : null}
		</VStack>
	);

	return (
		<BackgroundContainer
			{...extractBackgroundContainerProps(config)}
			position="relative"
			className={isPageHeight ? pageHeightStyles : ""}
		>
			<ResponsiveContainer zIndex="2" h="100%">
				<SectionContainer
					{...extractSectionContainerProps(config)}
					height="100%"
					display="flex"
					alignItems="center"
					className={sectionStyles}
				>
					{isContentLargeImageVariant ? (
						<Flex
							direction={{ base: flip ? "row-reverse" : "row", mdDown: "column" }}
							justifyContent="space-between"
							alignItems={verticallyCenter ? "center" : undefined}
							columnGap="eds.dimension.64"
							rowGap={{ md: "eds.dimension.80", mdDown: "eds.dimension.48" }}
							width="100%"
						>
							{content}
							{hasAsset ? (
								<Box
									key={asset?.url}
									className={cx(
										"asset-wrap",
										assetWrapStyles,
										css({ smDown: { display: "none" }, sm: { display: "block" } }),
									)}
								>
									<Wrap
										condition={isFirstSection}
										wrap={(children) => (
											<Animate delay={0.5} type="slideLeft" customCss={animateCustomCss}>
												{children}
											</Animate>
										)}
									>
										<ContentLargeImageMedia asset={asset} isFirstSection={isFirstSection} />
									</Wrap>
								</Box>
							) : null}
						</Flex>
					) : (
						content
					)}
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
